import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Trash2, GripVertical, X, Pencil, RefreshCw, Check, Plus, ImageIcon, Upload, ArrowUp, ArrowLeft, Sparkles, ShoppingBag, User } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

const GENERATE_VIDEO_URL = 'https://ai-agency-dt7d.onrender.com/webhook/start_video_cine';

interface Scene {
  index: number;
  prompt: string;
  start_frame: string | null;
  end_frame: string | null;
  duration_seconds: number;
  colorIndex: number;
}

interface PlanData {
  style_prompt: string;
  scenes: Scene[];
}

interface LocationState {
  planData: PlanData;
  prompt: string;
  durationMode: string;
  formato: string;
  modoPro: boolean;
  ids: string[];
}

// An "element" inside a scene: either a frame image or a prompt text
type ElementType = 'start_frame' | 'end_frame' | 'prompt';

interface SceneElement {
  sceneIdx: number;
  type: ElementType;
  content: string; // b64 for frames, text for prompt
}

// Pastel scene colors — cycle through them
const SCENE_COLORS = [
  { bg: 'bg-green-100/60', ring: 'ring-green-300/40', label: 'text-green-700', border: 'border-green-300/50' },
  { bg: 'bg-blue-100/60', ring: 'ring-blue-300/40', label: 'text-blue-700', border: 'border-blue-300/50' },
  { bg: 'bg-purple-100/60', ring: 'ring-purple-300/40', label: 'text-purple-700', border: 'border-purple-300/50' },
  { bg: 'bg-amber-100/60', ring: 'ring-amber-300/40', label: 'text-amber-700', border: 'border-amber-300/50' },
  { bg: 'bg-rose-100/60', ring: 'ring-rose-300/40', label: 'text-rose-700', border: 'border-rose-300/50' },
  { bg: 'bg-cyan-100/60', ring: 'ring-cyan-300/40', label: 'text-cyan-700', border: 'border-cyan-300/50' },
];

const DURATION_STEPS = [6, 8, 10];
const PX_PER_SEC_DESKTOP = 66; // desktop: pixels per second on timeline
const SCENE_GAP_PX = 36; // gap between scenes on desktop timeline

const PlanificadorVideoCinematografico: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  const state = location.state as LocationState | null;

  const [scenes, setScenes] = useState<Scene[]>(() => {
    const raw = state?.planData?.scenes || [];
    return raw.map((s, i) => ({ ...s, colorIndex: s.colorIndex ?? i % SCENE_COLORS.length }));
  });
  const [stylePrompt] = useState<string>(state?.planData?.style_prompt || '');

  // Element drag state (drag individual elements within a scene)
  const [dragKey, setDragKey] = useState<string | null>(null); // "sceneIdx-type"
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchDragKey = useRef<string | null>(null);
  const elementRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Mobile duration dropdown state
  const [mobileDurationOpen, setMobileDurationOpen] = useState<number | null>(null);

  // Desktop expanded prompt editor (key = "sceneIdx-prompt")
  const [expandedPromptKey, setExpandedPromptKey] = useState<string | null>(null);

  // Frame context menu (key = "sceneIdx-start_frame" or "sceneIdx-end_frame")
  const [frameMenuKey, setFrameMenuKey] = useState<string | null>(null);

  // Add-image dropdown (key = sceneIdx)
  const [addImageMenu, setAddImageMenu] = useState<number | null>(null);

  // Image picker modal target: { sceneIdx, frameType }
  const [imagePickerTarget, setImagePickerTarget] = useState<{ sceneIdx: number; frameType: 'start_frame' | 'end_frame' } | null>(null);

  // Company ID for fetching images
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Image picker modal states
  type PickerScreen = 'gallery' | 'confirm' | 'edit' | 'generate';
  const [pickerScreen, setPickerScreen] = useState<PickerScreen>('gallery');
  const [pickerSelectedImage, setPickerSelectedImage] = useState<{ url: string; id?: string; isBase64?: boolean } | null>(null);
  const [generateInstruction, setGenerateInstruction] = useState('');
  const [historyImages, setHistoryImages] = useState<{ id: string; url: string; fileName: string }[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const historyRowsRef = useRef<{ id: string; file_name: string }[]>([]);
  const historyOffsetRef = useRef(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const HISTORY_PAGE_SIZE = 12;
  const [editInstruction, setEditInstruction] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerAnimated, setPickerAnimated] = useState(false);
  const [pickerDragY, setPickerDragY] = useState(0);
  const pickerDragStartY = useRef<number | null>(null);

  // Fetch companyId on mount
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        if (profile?.id_company) setCompanyId(profile.id_company as string);
      } catch {}
    };
    fetchCompany();
  }, []);

  // Resolve signed URLs for a batch of rows (parallel)
  const resolveSignedUrls = useCallback(async (rows: { id: string; file_name: string }[], cId: string) => {
    const results: { id: string; url: string; fileName: string }[] = [];
    await Promise.all(rows.map(async (row) => {
      const fn = row.file_name;
      if (!fn) return;
      const optPath = `${cId}/opt/${fn}`;
      const { data: optData } = await supabase.storage.from('generadas').createSignedUrl(optPath, 3600);
      if (optData?.signedUrl) {
        results.push({ id: row.id, url: optData.signedUrl, fileName: fn });
      } else {
        const fullPath = `${cId}/full/${fn}`;
        const { data: fullData } = await supabase.storage.from('generadas').createSignedUrl(fullPath, 3600);
        if (fullData?.signedUrl) {
          results.push({ id: row.id, url: fullData.signedUrl, fileName: fn });
        }
      }
    }));
    return results;
  }, []);

  // Load history images when picker opens (first page only)
  useEffect(() => {
    if (!imagePickerTarget || !companyId) return;
    setPickerScreen('gallery');
    setPickerSelectedImage(null);
    setEditInstruction('');
    setHistoryImages([]);
    historyRowsRef.current = [];
    historyOffsetRef.current = 0;
    setHasMoreHistory(false);

    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const { data: rows } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name, tipo')
          .eq('id_company', companyId)
          .eq('active', true)
          .order('created_at', { ascending: false })
          .limit(200);

        if (!rows || rows.length === 0) { setHistoryImages([]); return; }

        // Filter out videos and rows without file_name
        const imageRows = rows
          .filter(r => r.tipo !== 'video' && r.tipo !== 'video_pro' && r.file_name)
          .map(r => ({ id: r.id as string, file_name: r.file_name as string }));

        historyRowsRef.current = imageRows;

        // Resolve first page
        const firstBatch = imageRows.slice(0, HISTORY_PAGE_SIZE);
        const resolved = await resolveSignedUrls(firstBatch, companyId);
        historyOffsetRef.current = HISTORY_PAGE_SIZE;
        setHasMoreHistory(imageRows.length > HISTORY_PAGE_SIZE);
        setHistoryImages(resolved);
      } catch (err) {
        console.error('Error fetching history images:', err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePickerTarget, companyId]);

  // Load next page of history images
  const loadMoreHistoryImages = useCallback(async () => {
    if (!companyId || loadingMoreHistory) return;
    const allRows = historyRowsRef.current;
    const offset = historyOffsetRef.current;
    if (offset >= allRows.length) { setHasMoreHistory(false); return; }

    setLoadingMoreHistory(true);
    try {
      const nextBatch = allRows.slice(offset, offset + HISTORY_PAGE_SIZE);
      const resolved = await resolveSignedUrls(nextBatch, companyId);
      historyOffsetRef.current = offset + HISTORY_PAGE_SIZE;
      setHasMoreHistory(offset + HISTORY_PAGE_SIZE < allRows.length);
      setHistoryImages(prev => [...prev, ...resolved]);
    } catch (err) {
      console.error('Error loading more history images:', err);
    } finally {
      setLoadingMoreHistory(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, loadingMoreHistory, resolveSignedUrls]);

  // Animate picker panel open
  useEffect(() => {
    if (imagePickerTarget) {
      requestAnimationFrame(() => setPickerAnimated(true));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [imagePickerTarget]);

  const closePickerPanel = useCallback(() => {
    setPickerAnimated(false);
    setTimeout(() => setImagePickerTarget(null), 300);
  }, []);

  const handlePickerTouchStart = (e: React.TouchEvent) => {
    pickerDragStartY.current = e.touches[0].clientY;
  };
  const handlePickerTouchMove = (e: React.TouchEvent) => {
    if (pickerDragStartY.current === null) return;
    const delta = e.touches[0].clientY - pickerDragStartY.current;
    if (delta > 0) setPickerDragY(delta);
  };
  const handlePickerTouchEnd = () => {
    if (pickerDragY > 100) closePickerPanel();
    else setPickerDragY(0);
    pickerDragStartY.current = null;
  };

  // Desktop canvas state — pan & zoom
  const initialPanComputed = useRef(false);
  const [canvasPan, setCanvasPan] = useState<{ x: number; y: number }>({ x: 40, y: 40 });
  const [canvasZoom, setCanvasZoom] = useState<number>(1);
  const canvasPanning = useRef<boolean>(false);
  const canvasPanStart = useRef<{ x: number; y: number; panX: number; panY: number }>({ x: 0, y: 0, panX: 0, panY: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);

  // Generation state
  const [generating, setGenerating] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const generatingLockRef = useRef<boolean>(false);

  // Inline video results
  const [showResults, setShowResults] = useState(false);
  const [generatedVideoId, setGeneratedVideoId] = useState<string>('');
  const [companyIdForVideo, setCompanyIdForVideo] = useState<string>('');
  const willShowResultsRef = useRef<boolean>(false);

  // Set mobile header
  useEffect(() => {
    setMobileTitleOverride('Diseña tu video');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  // Redirect if no state
  useEffect(() => {
    if (!state?.planData?.scenes) {
      navigate(-1);
    }
  }, [state, navigate]);

  // Center timeline on initial load — callback ref approach
  const canvasCallbackRef = useCallback((node: HTMLDivElement | null) => {
    (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (!node || initialPanComputed.current) return;

    const tryCenter = () => {
      if (initialPanComputed.current) return;
      const w = node.clientWidth;
      const h = node.clientHeight;
      if (w > 0 && h > 0) {
        initialPanComputed.current = true;
        const initTotal = scenes.reduce((acc, s) => acc + s.duration_seconds, 0);
        const timelineW = initTotal * PX_PER_SEC_DESKTOP + Math.max(0, scenes.length - 1) * SCENE_GAP_PX;
        const timelineH = 320;
        setCanvasPan({
          x: Math.max(40, (w - timelineW) / 2),
          y: Math.max(40, (h - timelineH) / 2),
        });
      }
    };

    // Try immediately, then also after a frame
    tryCenter();
    if (!initialPanComputed.current) {
      requestAnimationFrame(tryCenter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalDuration = scenes.reduce((acc, s) => acc + s.duration_seconds, 0);

  // Cumulative time offsets for timeline ruler
  const cumulativeTimes = useMemo(() => {
    const times: number[] = [0];
    scenes.forEach((s) => { times.push(times[times.length - 1] + s.duration_seconds); });
    return times;
  }, [scenes]);

  // Duration resize state
  const resizingScene = useRef<number | null>(null);
  const resizeAnchorX = useRef<number>(0);
  const resizeCurrentDur = useRef<number>(0);

  // Desktop: mouse-based duration resize (drag right border)
  // Each 2-second step = PX_PER_SEC_DESKTOP * 2 pixels. Snap threshold = half a step.
  const handleResizeMouseDown = (sceneIdx: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizingScene.current = sceneIdx;
    resizeAnchorX.current = e.clientX;
    resizeCurrentDur.current = scenes[sceneIdx].duration_seconds;

    const stepPx = PX_PER_SEC_DESKTOP * 2 * canvasZoom; // pixels per duration step (2s)
    const snapThreshold = stepPx * 0.4; // 40% of a step to snap

    const handleMouseMove = (ev: MouseEvent) => {
      if (resizingScene.current === null) return;
      const delta = ev.clientX - resizeAnchorX.current;
      const curIdx = DURATION_STEPS.indexOf(resizeCurrentDur.current);
      if (curIdx === -1) return;

      let newIdx = curIdx;
      if (delta > snapThreshold && curIdx < DURATION_STEPS.length - 1) {
        newIdx = curIdx + 1;
      } else if (delta < -snapThreshold && curIdx > 0) {
        newIdx = curIdx - 1;
      }

      if (newIdx !== curIdx) {
        const newDur = DURATION_STEPS[newIdx];
        resizeCurrentDur.current = newDur;
        resizeAnchorX.current = ev.clientX; // reset anchor for continuous dragging
        const idx = resizingScene.current;
        setScenes(prev => prev.map((s, i) => i === idx ? { ...s, duration_seconds: newDur } : s));
      }
    };

    const handleMouseUp = () => {
      resizingScene.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };

    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };


  // Build flat element list per scene (only elements that actually exist)
  const sceneElements = useMemo(() => {
    return scenes.map((scene, sIdx) => {
      const elements: SceneElement[] = [];
      if (scene.start_frame) elements.push({ sceneIdx: sIdx, type: 'start_frame', content: scene.start_frame });
      elements.push({ sceneIdx: sIdx, type: 'prompt', content: scene.prompt });
      if (scene.end_frame) elements.push({ sceneIdx: sIdx, type: 'end_frame', content: scene.end_frame });
      return elements;
    });
  }, [scenes]);

  const makeKey = (sIdx: number, type: ElementType) => `${sIdx}-${type}`;

  // Desktop canvas pan handlers
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Close expanded prompt editor on any canvas click outside it and outside prompt cards
    if (expandedPromptKey && !target.closest('[data-prompt-editor]') && !target.closest('[data-prompt-card]')) {
      setExpandedPromptKey(null);
    }

    // Close frame context menu and add-image menu on any click outside them
    if (frameMenuKey) setFrameMenuKey(null);
    if (addImageMenu !== null && !target.closest('[data-add-image-menu]')) setAddImageMenu(null);

    // Only pan on middle-click or when clicking on the canvas bg (not on elements)
    const isCanvasBg = target === canvasRef.current || target.dataset.canvasBg === 'true';
    if (e.button === 1 || (e.button === 0 && isCanvasBg)) {
      e.preventDefault();
      canvasPanning.current = true;
      canvasPanStart.current = { x: e.clientX, y: e.clientY, panX: canvasPan.x, panY: canvasPan.y };
    }
  }, [canvasPan, expandedPromptKey, frameMenuKey, addImageMenu]);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (!canvasPanning.current) return;
    const dx = e.clientX - canvasPanStart.current.x;
    const dy = e.clientY - canvasPanStart.current.y;
    setCanvasPan({ x: canvasPanStart.current.panX + dx, y: canvasPanStart.current.panY + dy });
  }, []);

  const handleCanvasMouseUp = useCallback(() => {
    canvasPanning.current = false;
  }, []);

  const handleCanvasWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setCanvasZoom(prev => Math.min(2, Math.max(0.3, prev + delta)));
  }, []);

  // Desktop element drag handlers
  const handleElDragStart = (key: string) => (e: React.DragEvent) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleElDragOver = (key: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== dragKey) setDragOverKey(key);
  };

  const handleElDragEnd = () => {
    if (dragKey && dragOverKey && dragKey !== dragOverKey) {
      reorderElement(dragKey, dragOverKey);
    }
    setDragKey(null);
    setDragOverKey(null);
  };

  // Mobile touch drag
  const handleElTouchStart = (key: string) => (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchDragKey.current = key;
    setDragKey(key);
  };

  const handleElTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchDragKey.current) return;
    const y = e.touches[0].clientY;
    const x = e.touches[0].clientX;

    // Hit test all element refs
    for (const [key, el] of elementRefs.current.entries()) {
      if (key === touchDragKey.current) continue;
      const rect = el.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom && x >= rect.left && x <= rect.right) {
        setDragOverKey(key);
        return;
      }
    }
  }, []);

  const handleElTouchEnd = useCallback(() => {
    if (touchDragKey.current && dragOverKey && touchDragKey.current !== dragOverKey) {
      reorderElement(touchDragKey.current, dragOverKey);
    }
    touchStartY.current = null;
    touchDragKey.current = null;
    setDragKey(null);
    setDragOverKey(null);
  }, [dragOverKey, scenes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reorder: move element from source position to target position within the same scene
  const reorderElement = (fromKey: string, toKey: string) => {
    const [fromSceneStr, ...fromTypeParts] = fromKey.split('-');
    const [toSceneStr, ...toTypeParts] = toKey.split('-');
    const fromScene = parseInt(fromSceneStr);
    const toScene = parseInt(toSceneStr);
    const fromType = fromTypeParts.join('-') as ElementType;
    const toType = toTypeParts.join('-') as ElementType;

    // Only allow reorder within the same scene
    if (fromScene !== toScene) return;

    const scene = { ...scenes[fromScene] };

    // Get current element order and swap
    type FieldKey = 'start_frame' | 'prompt' | 'end_frame';
    const fieldMap: Record<ElementType, FieldKey> = {
      start_frame: 'start_frame',
      end_frame: 'end_frame',
      prompt: 'prompt',
    };

    // Swap the two fields
    const fromField = fieldMap[fromType];
    const toField = fieldMap[toType];
    if (fromField === toField) return;

    // Only swap frames with frames (don't swap prompt with frame)
    if ((fromType === 'prompt') !== (toType === 'prompt')) return;

    const temp = (scene as any)[fromField];
    (scene as any)[fromField] = (scene as any)[toField];
    (scene as any)[toField] = temp;

    const updated = [...scenes];
    updated[fromScene] = scene;
    setScenes(updated);
  };

  const handleDeleteScene = (idx: number) => {
    const updated = scenes.filter((_, i) => i !== idx).map((s, i) => ({ ...s, index: i }));
    setScenes(updated);
  };

  const handleAddScene = (afterIdx: number) => {
    // Pick next color that isn't used by neighbors
    const usedColors = new Set(scenes.map(s => s.colorIndex));
    let newColor = 0;
    for (let i = 0; i < SCENE_COLORS.length; i++) {
      if (!usedColors.has(i)) { newColor = i; break; }
      newColor = i;
    }
    // If all colors used, just pick the next in cycle after the scene before
    if (usedColors.size >= SCENE_COLORS.length) {
      const prevColor = scenes[afterIdx]?.colorIndex ?? 0;
      newColor = (prevColor + 1) % SCENE_COLORS.length;
    }
    const newScene: Scene = {
      index: 0,
      prompt: '',
      start_frame: null,
      end_frame: null,
      duration_seconds: DURATION_STEPS[0],
      colorIndex: newColor,
    };
    const updated = [
      ...scenes.slice(0, afterIdx + 1),
      newScene,
      ...scenes.slice(afterIdx + 1),
    ].map((s, i) => ({ ...s, index: i }));
    setScenes(updated);
    // Auto-expand the new scene's prompt for editing
    setExpandedPromptKey(`${afterIdx + 1}-prompt`);
  };

  const handleRemoveElement = (sceneIdx: number, type: ElementType) => {
    const updated = [...scenes];
    const scene = { ...updated[sceneIdx] };
    if (type === 'start_frame') scene.start_frame = null;
    else if (type === 'end_frame') scene.end_frame = null;
    else if (type === 'prompt') scene.prompt = '';
    updated[sceneIdx] = scene;
    setScenes(updated);
  };

  // === Image Picker handlers ===

  // Get images currently used in this video's scenes (start/end frames)
  const usedInVideoImages = useMemo(() => {
    const imgs: { label: string; b64: string }[] = [];
    scenes.forEach((s, i) => {
      if (s.start_frame) imgs.push({ label: `Escena ${i + 1} — Imagen inicial`, b64: s.start_frame });
      if (s.end_frame) imgs.push({ label: `Escena ${i + 1} — Imagen final`, b64: s.end_frame });
    });
    return imgs;
  }, [scenes]);

  // Handle file upload — convert to base64
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPickerSelectedImage({ url: result, isBase64: true });
      setPickerScreen('confirm');
      setUploadingFile(false);
    };
    reader.onerror = () => setUploadingFile(false);
    reader.readAsDataURL(file);
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  // Apply the selected image to the scene as a frame
  const handleApplyImage = () => {
    if (!imagePickerTarget || !pickerSelectedImage) return;
    const { sceneIdx, frameType } = imagePickerTarget;

    let b64: string;
    if (pickerSelectedImage.isBase64) {
      // Already a data URL or base64
      const url = pickerSelectedImage.url;
      b64 = url.includes(',') ? url.split(',')[1] : url;
    } else {
      // It's a signed URL — we need to convert it; for now store the URL
      // Actually, scenes store base64. We need to fetch and convert.
      // This will be handled by fetching the full-res image and converting.
      b64 = pickerSelectedImage.url; // placeholder, we'll fix with actual fetch
    }

    setScenes(prev => prev.map((s, i) => {
      if (i !== sceneIdx) return s;
      return { ...s, [frameType]: b64 };
    }));
    setImagePickerTarget(null);
  };

  // Apply image from URL (fetch, convert to base64, then apply)
  const handleApplyImageFromUrl = async (url: string) => {
    if (!imagePickerTarget) return;
    const { sceneIdx, frameType } = imagePickerTarget;
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const b64 = result.includes(',') ? result.split(',')[1] : result;
        setScenes(prev => prev.map((s, i) => {
          if (i !== sceneIdx) return s;
          return { ...s, [frameType]: b64 };
        }));
        setImagePickerTarget(null);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error fetching image for frame:', err);
    }
  };

  // Edit image using the same endpoint as EditarImagen (edit_general mode)
  const handleEditImage = async () => {
    if (!pickerSelectedImage || !editInstruction.trim() || !companyId) return;
    setEditSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user');

      // We need the image ID for the endpoint. If the selected image has an id, use it.
      // If it's an uploaded file, we don't have an id — skip editing (shouldn't happen in normal flow).
      const imgId = pickerSelectedImage.id;
      if (!imgId) throw new Error('No image ID for editing');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/editor_ia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({
          user_id: user.id,
          imagen_id: imgId,
          instruction: editInstruction.trim(),
          mode: 'edit_general',
        }),
      });

      if (!response.ok) throw new Error('Error al editar la imagen');
      const data = await response.json();

      const newImageIdRaw: string | number | undefined = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageIdRaw) throw new Error('No edited image ID returned');

      // Resolve the new image row
      const newIdStr = String(newImageIdRaw);
      let newRow: { id: string; file_name: string } | null = null;
      const { data: idMatch } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name')
        .eq('id', newIdStr)
        .eq('id_company', companyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      if (!newRow) {
        const { data: exact } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('file_name', newIdStr)
          .eq('id_company', companyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      if (!newRow?.file_name) throw new Error('Could not find edited image');

      // Get the full signed URL for the edited image
      const fullPath = `${companyId}/full/${newRow.file_name}`;
      const { data: signedData } = await supabase.storage.from('generadas').createSignedUrl(fullPath, 3600);
      if (!signedData?.signedUrl) throw new Error('Could not get signed URL');

      // Update picker to show the new edited image in confirm screen
      setPickerSelectedImage({ url: signedData.signedUrl, id: newRow.id as string });
      setEditInstruction('');
      setPickerScreen('confirm');
    } catch (err) {
      console.error('Error editing image:', err);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleGenerate = async () => {
    if (generatingLockRef.current || scenes.length === 0) return;
    generatingLockRef.current = true;

    try {
      setGenError(null);
      setGenerating(true);
      setCompleteProgressNow(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');
      const compId = profile.id_company as string;

      const formatMap: Record<string, string> = { story: '9:16', square: '1:1', post: '4:5' };

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: state?.ids && state.ids.length > 0 ? state.ids : undefined,
        tipo_video: 'cinematografico',
        prompt: state?.prompt || '',
        duracion_mode: state?.durationMode || 'unica',
        Format: formatMap[state?.formato || 'story'] || '9:16',
        modo_pro: state?.modoPro || false,
        style_prompt: stylePrompt,
        scenes: scenes.map(s => ({
          index: s.index,
          prompt: s.prompt,
          start_frame: s.start_frame,
          end_frame: s.end_frame,
          duration_seconds: s.duration_seconds,
        })),
      };

      console.log('[PlanificadorVideoCinematografico] handleGenerate payload', requestData);

      const response = await fetch(GENERATE_VIDEO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.clone().text(); } catch {}
      console.log('[PlanificadorVideoCinematografico] response', { status: response.status, ok: response.ok, text: respText });

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[PlanificadorVideoCinematografico] json parse error', e); }

      if (data?.response === 'success' && data?.video_generado_id) {
        const videoId = data.video_generado_id as string;
        willShowResultsRef.current = true;
        setGeneratedVideoId(videoId);
        setCompanyIdForVideo(compId);
        setCompleteProgressNow(true);
      } else {
        throw new Error('No se recibió el video generado.');
      }
    } catch (e: any) {
      console.error('[PlanificadorVideoCinematografico] handleGenerate error', e);
      setGenError('Se produjo un error. Por favor, volvé a intentarlo.');
      setGenerating(false);
      generatingLockRef.current = false;
    }
  };

  if (showResults) {
    return (
      <GeneratedImagesInline
        imageIds={[generatedVideoId]}
        companyId={companyIdForVideo}
        onClose={() => {
          setShowResults(false);
        }}
        onReturnToConfig={() => {
          mergeLastCrearAnuncioState({ selectedVariantIds: [] });
          navigate('/redes-sociales/crear-anuncio/seleccionar-productos');
        }}
        onReady={() => {}}
      />
    );
  }

  const canGenerate = scenes.length > 0 && !generating;

  // Render a single element card (frame image or prompt text)
  const renderElementCard = (el: SceneElement, _sceneColor: typeof SCENE_COLORS[number], isDesktop: boolean) => {
    const key = makeKey(el.sceneIdx, el.type);
    const isDragged = dragKey === key;
    const isDragOver = dragOverKey === key && dragKey !== key;
    const isFrame = el.type === 'start_frame' || el.type === 'end_frame';
    const frameLabel = el.type === 'start_frame' ? 'Imagen inicial' : el.type === 'end_frame' ? 'Imagen final' : null;

    return (
      <div
        key={key}
        ref={(node) => { if (node) elementRefs.current.set(key, node); else elementRefs.current.delete(key); }}
        draggable={isDesktop}
        onDragStart={isDesktop ? handleElDragStart(key) : undefined}
        onDragOver={isDesktop ? handleElDragOver(key) : undefined}
        onDragEnd={isDesktop ? handleElDragEnd : undefined}
        onTouchStart={!isDesktop ? handleElTouchStart(key) : undefined}
        onTouchMove={!isDesktop ? handleElTouchMove : undefined}
        onTouchEnd={!isDesktop ? handleElTouchEnd : undefined}
        className={`group relative rounded-[10px] bg-white transition-all ${
          isDragged ? 'opacity-50 scale-[0.97]' : ''
        } ${isDragOver ? 'ring-2 ring-brand-primary/40' : 'shadow-[0_1px_3px_rgba(0,0,0,0.06)]'}
        ${isDesktop ? 'cursor-grab active:cursor-grabbing' : ''}`}
      >
        {/* Drag handle indicator */}
        <div className={`absolute top-1.5 left-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity ${!isDesktop ? 'hidden' : ''}`}>
          <GripVertical className="w-3.5 h-3.5 text-brand-black/20" />
        </div>

        {isFrame ? (
          // Frame element — image card with context menu on click
          <div
            className={`relative ${isDesktop ? 'h-[135px]' : ''} cursor-pointer`}
            onClick={(e) => { e.stopPropagation(); setFrameMenuKey(frameMenuKey === key ? null : key); }}
          >
            <img
              src={`data:image/png;base64,${el.content}`}
              alt={frameLabel || 'Frame'}
              className={`w-full object-cover rounded-[10px] ${isDesktop ? 'h-full' : 'aspect-[4/3]'}`}
            />
            {/* Frame type label */}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent rounded-b-[10px] px-2.5 pb-2 pt-5">
              <span className="text-[11px] font-medium text-white/90">{frameLabel}</span>
            </div>

            {/* Frame context menu */}
            {frameMenuKey === key && (
              <div
                className={`absolute z-20 bg-white rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.12)] overflow-hidden min-w-[140px] ${isDesktop ? 'top-full mt-1 left-0' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => { handleRemoveElement(el.sceneIdx, el.type); setFrameMenuKey(null); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/80 hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                    <Trash2 className="w-3.5 h-3.5 text-neutral-600" />
                  </div>
                  Eliminar
                </button>
                <div className="h-[0.5px] bg-black/[0.06] mx-3" />
                <button
                  type="button"
                  disabled
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/30 cursor-not-allowed"
                >
                  <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                    <Pencil className="w-3.5 h-3.5 text-neutral-400" />
                  </div>
                  Editar
                </button>
                <div className="h-[0.5px] bg-black/[0.06] mx-3" />
                <button
                  type="button"
                  disabled
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/30 cursor-not-allowed"
                >
                  <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                    <RefreshCw className="w-3.5 h-3.5 text-neutral-400" />
                  </div>
                  Reemplazar
                </button>
              </div>
            )}
          </div>
        ) : (
          // Prompt element — text card (clickable to expand editor on both mobile & desktop)
          <div
            data-prompt-card
            className={`px-3.5 py-3 ${isDesktop ? 'h-[135px] overflow-hidden cursor-pointer hover:bg-neutral-50/50 transition-colors' : 'min-h-[60px]'}`}
            onClick={(e) => { e.stopPropagation(); setExpandedPromptKey(expandedPromptKey === key ? null : key); }}
          >
            {!isDesktop && expandedPromptKey === key ? (
              <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                <textarea
                  className="w-full min-h-[120px] text-[15px] text-brand-black/80 leading-relaxed bg-transparent resize-none focus:outline-none"
                  placeholder="Escribe las instrucciones para esta escena..."
                  value={scenes[el.sceneIdx].prompt}
                  onChange={(e) => {
                    const val = e.target.value;
                    setScenes(prev => prev.map((s, i) => i === el.sceneIdx ? { ...s, prompt: val } : s));
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setExpandedPromptKey(null)}
                  className={`self-end w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center ${_sceneColor.label}`}
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <p className={`text-[15px] leading-relaxed ${isDesktop ? 'line-clamp-5' : ''} ${el.content ? 'text-brand-black/80' : 'text-brand-black/30 italic'}`}>
                {el.content || 'Toca para escribir instrucciones...'}
              </p>
            )}
          </div>
        )}

        {/* Remove element button — only for prompts (frames use context menu) */}
        {!isFrame && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleRemoveElement(el.sceneIdx, el.type); }}
            className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand-black/60 text-white flex items-center justify-center z-10 transition-opacity ${
              isDesktop ? 'opacity-0 group-hover:opacity-100' : 'opacity-70'
            }`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-[100dvh] md:min-h-screen bg-[#F2F2F7]">
      {/* Desktop sticky header */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-5xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Diseña tu video</h1>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate}
                className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-md text-[15px] font-medium transition-all ${
                  !canGenerate
                    ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                    : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white shadow-sm'
                }`}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : 'Generar video'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============ MOBILE: Normal scrollable content ============ */}
      <div className="sm:hidden">
        <div className="max-w-5xl mx-auto px-4 pt-1 pb-28">

          {/* Style prompt summary */}
          {stylePrompt && (
            <div className="mt-2">
              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Estilo visual</p>
              <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-3">
                  <p className="text-[15px] text-brand-black/90 leading-relaxed">{stylePrompt}</p>
                </div>
              </div>
            </div>
          )}

          {/* Summary bar */}
          <div className="mt-4 px-4 flex items-center justify-between">
            <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">
              {scenes.length} {scenes.length === 1 ? 'escena' : 'escenas'} · {totalDuration}s total
            </p>
          </div>

          {/* Mobile scene list */}
          <div className="mt-4 space-y-4">
            {scenes.map((scene, sIdx) => {
              const color = SCENE_COLORS[scene.colorIndex % SCENE_COLORS.length];
              const elements = sceneElements[sIdx] || [];
              const isDropdownOpen = mobileDurationOpen === sIdx;

              return (
                <React.Fragment key={scene.index}>
                {/* Insert button between scenes (mobile) */}
                {sIdx > 0 && (
                  <div className="flex justify-center -my-1">
                    <button
                      type="button"
                      onClick={() => handleAddScene(sIdx - 1)}
                      className="w-6 h-6 rounded-full bg-black/[0.03] flex items-center justify-center text-brand-black/15 active:bg-black/[0.08] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div>
                  {/* Scene container — pastel color */}
                  <div className={`${color.bg} rounded-[13px] p-2.5 space-y-2.5`}>
                    {/* Scene label + duration dropdown + delete (inside container) */}
                    <div className="flex items-center gap-2">
                      <span className={`text-[15px] font-semibold ${color.label}`}>Escena {sIdx + 1}</span>

                      {/* Duration mini-dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setMobileDurationOpen(isDropdownOpen ? null : sIdx)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)] text-[13px] font-medium text-brand-black/70 active:bg-neutral-50 transition-colors"
                        >
                          <span className="tabular-nums">{scene.duration_seconds}s</span>
                          <svg className={`w-3 h-3 text-brand-black/40 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Dropdown options */}
                        {isDropdownOpen && (
                          <div className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.12)] overflow-hidden z-20 min-w-[72px]">
                            {DURATION_STEPS.map((dur) => (
                              <button
                                key={dur}
                                type="button"
                                onClick={() => {
                                  setScenes(prev => prev.map((s, i) => i === sIdx ? { ...s, duration_seconds: dur } : s));
                                  setMobileDurationOpen(null);
                                }}
                                className={`w-full px-3 py-2 text-left text-[13px] font-medium tabular-nums transition-colors ${
                                  scene.duration_seconds === dur
                                    ? 'text-brand-primary bg-brand-primary/[0.06]'
                                    : 'text-brand-black/70 active:bg-neutral-50'
                                }`}
                              >
                                {dur}s
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Add image button — only if at least one frame slot is empty */}
                      {(!scene.start_frame || !scene.end_frame) && (
                        <div className="ml-auto relative">
                          <button
                            type="button"
                            onClick={() => setAddImageMenu(addImageMenu === sIdx ? null : sIdx)}
                            className={`p-1.5 rounded-sm ${color.label} active:bg-white/30 transition-colors`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                          {addImageMenu === sIdx && (
                            <>
                              <div className="fixed inset-0 z-[5]" onClick={() => setAddImageMenu(null)} />
                              <div className="absolute top-full right-0 mt-1 bg-white rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.12)] overflow-hidden z-10 min-w-[240px]">
                                {!scene.start_frame && (
                                  <button
                                    type="button"
                                    onClick={() => { setAddImageMenu(null); setImagePickerTarget({ sceneIdx: sIdx, frameType: 'start_frame' }); }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/80 active:bg-neutral-50 transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                                      <ImageIcon className="w-3.5 h-3.5 text-neutral-600" />
                                    </div>
                                    Añadir imagen inicial
                                  </button>
                                )}
                                {!scene.start_frame && !scene.end_frame && (
                                  <div className="h-[0.5px] bg-black/[0.06] mx-3" />
                                )}
                                {!scene.end_frame && (
                                  <button
                                    type="button"
                                    onClick={() => { setAddImageMenu(null); setImagePickerTarget({ sceneIdx: sIdx, frameType: 'end_frame' }); }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/80 active:bg-neutral-50 transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                                      <ImageIcon className="w-3.5 h-3.5 text-neutral-600" />
                                    </div>
                                    Añadir imagen final
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteScene(sIdx)}
                        className={`${scene.start_frame && scene.end_frame ? 'ml-auto' : ''} p-1.5 rounded-sm ${color.label} active:bg-white/30 transition-colors`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {elements.length === 0 ? (
                      <div className="rounded-[10px] bg-white/60 px-3.5 py-4 flex items-center justify-center">
                        <p className="text-[13px] text-[#3C3C43]/30">Sin elementos</p>
                      </div>
                    ) : (
                      elements.map((el) => renderElementCard(el, color, false))
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}

            {/* Add scene button after last scene (mobile) */}
            {scenes.length > 0 && (
              <div className="flex justify-center py-1">
                <button
                  type="button"
                  onClick={() => handleAddScene(scenes.length - 1)}
                  className="w-7 h-7 rounded-full bg-black/[0.03] flex items-center justify-center text-brand-black/15 active:bg-black/[0.08] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Empty state */}
          {scenes.length === 0 && (
            <div className="mt-4 rounded-[13px] bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)] p-8 flex flex-col items-center justify-center">
              <span className="material-symbols-rounded text-[40px] text-[#3C3C43]/20 mb-2">movie_filter</span>
              <p className="text-[15px] text-[#3C3C43]/60 text-center">No hay escenas. Volvé atrás para planificar tu video.</p>
            </div>
          )}
        </div>
      </div>

      {/* ============ DESKTOP: Full-screen canvas ============ */}
      <div
        className="hidden sm:block fixed top-14 bottom-0 right-0 overflow-hidden"
        style={{ left: sidebarWidth, cursor: canvasPanning.current ? 'grabbing' : 'grab' }}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        onWheel={handleCanvasWheel}
        ref={canvasCallbackRef}
      >
        {/* Canvas dot grid background */}
        <div
          className="absolute inset-0 pointer-events-none"
          data-canvas-bg="true"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px)',
            backgroundSize: `${24 * canvasZoom}px ${24 * canvasZoom}px`,
            backgroundPosition: `${canvasPan.x % (24 * canvasZoom)}px ${canvasPan.y % (24 * canvasZoom)}px`,
          }}
        />

        {/* Pannable & zoomable inner container */}
        <div
          className="absolute"
          style={{
            transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`,
            transformOrigin: '0 0',
          }}
        >
          {/* Horizontal timeline ruler */}
          <div className="relative" style={{ width: totalDuration * PX_PER_SEC_DESKTOP + Math.max(0, scenes.length - 1) * SCENE_GAP_PX, minHeight: 280, transition: 'width 300ms ease' }}>
            {/* Ruler bar */}
            <div className="absolute top-0 left-0 right-0 h-6 flex items-end">
              {/* Continuous ruler line */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black/[0.1]" />
              {/* Tick marks at cumulative times */}
              {cumulativeTimes.map((t, i) => (
                <div
                  key={`dtick-${i}`}
                  className="absolute bottom-0 flex flex-col items-center"
                  style={{ left: t * PX_PER_SEC_DESKTOP + i * SCENE_GAP_PX, transition: 'left 300ms ease' }}
                >
                  <span className="text-[11px] font-medium text-[#3C3C43]/50 tabular-nums mb-0.5 -translate-x-1/2">{t}s</span>
                  <div className="w-[2px] h-2 bg-black/[0.15]" />
                </div>
              ))}
            </div>

            {/* Scene blocks positioned on the timeline */}
            <div className="absolute top-8 left-0 right-0 flex" style={{ height: 'calc(100% - 32px)' }}>
              {scenes.map((scene, sIdx) => {
                const color = SCENE_COLORS[scene.colorIndex % SCENE_COLORS.length];
                const elements = sceneElements[sIdx] || [];
                const sceneWidth = scene.duration_seconds * PX_PER_SEC_DESKTOP;
                const sceneLeft = cumulativeTimes[sIdx] * PX_PER_SEC_DESKTOP + sIdx * SCENE_GAP_PX;

                return (
                  <div
                    key={scene.index}
                    className="absolute top-0 bottom-0 flex flex-col"
                    style={{ left: sceneLeft, width: sceneWidth, transition: 'left 300ms ease, width 300ms ease' }}
                  >
                    {/* Scene container — pastel color, horizontal layout, with right resize handle */}
                    <div className={`${color.bg} rounded-[13px] p-2.5 flex-1 flex flex-col relative`}>
                      {/* Scene label + duration + add image + delete (inside container) */}
                      <div className="flex items-center gap-1.5 mb-2 px-0.5">
                        <span className={`text-[13px] font-semibold ${color.label} whitespace-nowrap`}>Escena {sIdx + 1}</span>
                        <span className="text-[13px] font-medium text-[#3C3C43]/60 tabular-nums">{scene.duration_seconds}s</span>
                        {/* Add image button (desktop) — only if at least one frame slot is empty */}
                        {(!scene.start_frame || !scene.end_frame) && (
                          <div className="ml-auto relative">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setAddImageMenu(addImageMenu === sIdx ? null : sIdx); }}
                              className={`p-1 rounded-sm ${color.label} hover:bg-white/30 transition-colors`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                            {addImageMenu === sIdx && (
                              <div
                                data-add-image-menu
                                className="absolute top-full right-0 mt-1 bg-white rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.12)] overflow-hidden z-10 min-w-[240px]"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {!scene.start_frame && (
                                  <button
                                    type="button"
                                    onClick={() => { setAddImageMenu(null); setImagePickerTarget({ sceneIdx: sIdx, frameType: 'start_frame' }); }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                                      <ImageIcon className="w-3.5 h-3.5 text-neutral-600" />
                                    </div>
                                    Añadir imagen inicial
                                  </button>
                                )}
                                {!scene.start_frame && !scene.end_frame && (
                                  <div className="h-[0.5px] bg-black/[0.06] mx-3" />
                                )}
                                {!scene.end_frame && (
                                  <button
                                    type="button"
                                    onClick={() => { setAddImageMenu(null); setImagePickerTarget({ sceneIdx: sIdx, frameType: 'end_frame' }); }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                  >
                                    <div className="w-7 h-7 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                                      <ImageIcon className="w-3.5 h-3.5 text-neutral-600" />
                                    </div>
                                    Añadir imagen final
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteScene(sIdx)}
                          className={`${scene.start_frame && scene.end_frame ? 'ml-auto' : ''} p-1 rounded-sm ${color.label} hover:bg-white/30 transition-colors`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="flex-1 flex flex-row gap-2.5 items-stretch">
                      {elements.length === 0 ? (
                        <div className="rounded-[10px] bg-white/60 px-3.5 py-4 flex items-center justify-center flex-1">
                          <p className="text-[13px] text-[#3C3C43]/30">Sin elementos</p>
                        </div>
                      ) : (
                        elements.map((el) => (
                          <div key={makeKey(el.sceneIdx, el.type)} className={`${el.type === 'prompt' ? 'min-w-[100px] flex-1' : 'min-w-[100px] flex-1'}`}>
                            {renderElementCard(el, color, true)}
                          </div>
                        ))
                      )}
                      </div>

                      {/* Right resize handle (desktop) */}
                      <div
                        className="absolute top-0 -right-0 bottom-0 w-3 cursor-col-resize z-10 group/resize flex items-center justify-center hover:bg-black/[0.04] transition-colors"
                        onMouseDown={handleResizeMouseDown(sIdx)}
                      >
                        <div className="w-[3px] h-8 bg-black/[0.12] rounded-full group-hover/resize:bg-black/[0.25] transition-colors" />
                      </div>
                    </div>

                  </div>
                );
              })}

              {/* Insert buttons between scenes (desktop) */}
              {scenes.map((_, sIdx) => {
                if (sIdx === 0) return null;
                // Gap starts at the right edge of scene sIdx-1
                const gapLeft = cumulativeTimes[sIdx] * PX_PER_SEC_DESKTOP + (sIdx - 1) * SCENE_GAP_PX;
                return (
                  <div
                    key={`add-${sIdx}`}
                    className="absolute top-0 bottom-0 flex items-center justify-center"
                    style={{ left: gapLeft, width: SCENE_GAP_PX, transition: 'left 300ms ease' }}
                  >
                    <button
                      type="button"
                      onClick={() => handleAddScene(sIdx - 1)}
                      className="w-7 h-7 rounded-full bg-black/[0.03] flex items-center justify-center text-brand-black/15 hover:bg-black/[0.08] hover:text-brand-black/30 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}

              {/* Add scene button after last scene (desktop) */}
              {scenes.length > 0 && (
                <div
                  className="absolute top-0 bottom-0 flex items-center justify-center"
                  style={{ left: totalDuration * PX_PER_SEC_DESKTOP + Math.max(0, scenes.length - 1) * SCENE_GAP_PX + 8, transition: 'left 300ms ease' }}
                >
                  <button
                    type="button"
                    onClick={() => handleAddScene(scenes.length - 1)}
                    className="w-7 h-7 rounded-full bg-black/[0.03] flex items-center justify-center text-brand-black/15 hover:bg-black/[0.08] hover:text-brand-black/30 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Expanded prompt editor — below the timeline, aligned to clicked scene */}
          {expandedPromptKey && (() => {
            const [sceneIdxStr] = expandedPromptKey.split('-');
            const sceneIdx = parseInt(sceneIdxStr);
            if (isNaN(sceneIdx) || !scenes[sceneIdx]) return null;
            const editorLeft = cumulativeTimes[sceneIdx] * PX_PER_SEC_DESKTOP + sceneIdx * SCENE_GAP_PX;
            return (
              <div
                data-prompt-editor
                className="relative mt-6 rounded-[13px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.1)] overflow-hidden"
                style={{ width: 600, marginLeft: editorLeft }}
              >
                <button
                  type="button"
                  onClick={() => setExpandedPromptKey(null)}
                  className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-brand-black/[0.06] flex items-center justify-center text-brand-black/40 hover:text-brand-black/70 hover:bg-brand-black/[0.1] transition-colors z-10"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <textarea
                  className="w-full h-[200px] px-4 py-3.5 text-[15px] text-brand-black/80 leading-relaxed bg-transparent resize-none focus:outline-none"
                  placeholder="Escribe las instrucciones para esta escena..."
                  onMouseDown={(e) => e.stopPropagation()}
                  value={scenes[sceneIdx].prompt}
                  onChange={(e) => {
                    const val = e.target.value;
                    setScenes(prev => prev.map((s, i) => i === sceneIdx ? { ...s, prompt: val } : s));
                  }}
                  autoFocus
                />
              </div>
            );
          })()}

          {/* Empty state inside canvas */}
          {scenes.length === 0 && (
            <div className="rounded-[13px] bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)] p-8 flex flex-col items-center justify-center">
              <span className="material-symbols-rounded text-[40px] text-[#3C3C43]/20 mb-2">movie_filter</span>
              <p className="text-[15px] text-[#3C3C43]/60 text-center">No hay escenas. Volvé atrás para planificar tu video.</p>
            </div>
          )}
        </div>

        {/* Fixed overlay panels on top of canvas */}
        {/* Style prompt summary — top left */}
        {stylePrompt && (
          <div className="absolute top-4 left-4 max-w-sm z-10 pointer-events-auto">
            <p className="text-[11px] font-medium text-[#3C3C43]/40 uppercase tracking-wide mb-1">Estilo visual</p>
            <div className="rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] px-3.5 py-2.5">
              <p className="text-[13px] text-brand-black/80 leading-relaxed line-clamp-3">{stylePrompt}</p>
            </div>
          </div>
        )}

        {/* Summary bar — top right */}
        <div className="absolute top-4 right-4 z-10 pointer-events-auto">
          <div className="rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] px-3.5 py-2">
            <p className="text-[13px] font-medium text-brand-black/60">
              {scenes.length} {scenes.length === 1 ? 'escena' : 'escenas'} · {totalDuration}s
            </p>
          </div>
        </div>

        {/* Zoom controls — bottom right */}
        <div className="absolute bottom-4 right-4 z-10 pointer-events-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCanvasZoom(prev => Math.max(0.3, prev - 0.15))}
            className="w-8 h-8 rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] flex items-center justify-center text-brand-black/60 hover:text-brand-black/90 transition-colors"
          >
            <span className="text-[17px] font-medium leading-none">−</span>
          </button>
          <div className="px-2 py-1 rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
            <span className="text-[13px] font-medium text-brand-black/60 tabular-nums">{Math.round(canvasZoom * 100)}%</span>
          </div>
          <button
            type="button"
            onClick={() => setCanvasZoom(prev => Math.min(2, prev + 0.15))}
            className="w-8 h-8 rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] flex items-center justify-center text-brand-black/60 hover:text-brand-black/90 transition-colors"
          >
            <span className="text-[17px] font-medium leading-none">+</span>
          </button>
          <button
            type="button"
            onClick={() => { setCanvasPan({ x: 40, y: 40 }); setCanvasZoom(1); }}
            className="ml-1 px-2.5 h-8 rounded-md bg-white/90 backdrop-blur-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] flex items-center justify-center text-[13px] font-medium text-brand-black/60 hover:text-brand-black/90 transition-colors"
          >
            Resetear
          </button>
        </div>
      </div>

      {/* Mobile bottom bar — frosted glass */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            !canGenerate ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed' : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {generating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </span>
          ) : 'Generar video'}
        </button>
      </div>

      {/* Image Picker — Desktop Side Panel (rendered directly, portalled for z-index escape) */}
      {imagePickerTarget && createPortal(
        <>
          {/* Hidden file input */}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />

          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              pickerAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              {pickerScreen !== 'gallery' ? (
                <button
                  type="button"
                  onClick={() => {
                    if (pickerScreen === 'edit') { setPickerScreen('confirm'); setEditInstruction(''); }
                    else if (pickerScreen === 'generate') { setPickerScreen('gallery'); setGenerateInstruction(''); }
                    else { setPickerScreen('gallery'); setPickerSelectedImage(null); }
                  }}
                  className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Atrás</span>
                </button>
              ) : (
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  {imagePickerTarget.frameType === 'start_frame' ? 'Imagen inicial' : 'Imagen final'}
                </h3>
              )}
              <button type="button" onClick={closePickerPanel} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4" onScroll={(e) => {
              if (!hasMoreHistory || loadingMoreHistory || pickerScreen !== 'gallery') return;
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 150) loadMoreHistoryImages();
            }}>
              {pickerScreen === 'gallery' && (
                <div className="space-y-5">
                  <p className="text-[13px] text-neutral-500">Elegí una imagen existente o generá una nueva para usar como {imagePickerTarget.frameType === 'start_frame' ? 'inicio' : 'final'} de tu video.</p>
                  <div className="space-y-1">
                    <button type="button" onClick={() => { setPickerScreen('generate'); setGenerateInstruction(''); }} className="w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-brand-primary text-white">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <p className="text-[15px] font-medium text-neutral-900">Generar imagen</p>
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} className="w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
                        <Upload className="w-4 h-4" />
                      </div>
                      <p className="text-[15px] font-medium text-neutral-900">{uploadingFile ? 'Subiendo...' : 'Subir mi propia imagen'}</p>
                    </button>
                  </div>
                  {usedInVideoImages.length > 0 && (
                    <div>
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Usadas en este video</p>
                      <div className="grid grid-cols-3 gap-2">
                        {usedInVideoImages.map((img, idx) => (
                          <button key={`used-${idx}`} type="button" onClick={() => { setPickerSelectedImage({ url: `data:image/png;base64,${img.b64}`, isBase64: true }); setPickerScreen('confirm'); }} className="relative aspect-square rounded-sm overflow-hidden border border-neutral-200 hover:border-neutral-400 transition-colors">
                            <img src={`data:image/png;base64,${img.b64}`} alt={img.label} className="w-full h-full object-cover" />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent px-1.5 pb-1 pt-4">
                              <span className="text-[11px] font-medium text-white/90 line-clamp-1">{img.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Imágenes generadas</p>
                    {loadingHistory ? (
                      <div className="grid grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => (<div key={i} className="aspect-square bg-neutral-100 rounded-sm animate-pulse" />))}</div>
                    ) : historyImages.length === 0 ? (
                      <div className="text-center py-8"><ImageIcon className="w-10 h-10 text-neutral-300 mx-auto mb-2" /><p className="text-[13px] text-neutral-400">No hay imágenes generadas aún</p></div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          {historyImages.map((img) => (
                            <button key={img.id} type="button" onClick={() => { setPickerSelectedImage({ url: img.url, id: img.id }); setPickerScreen('confirm'); }} className="relative aspect-square rounded-sm overflow-hidden border border-neutral-200 hover:border-neutral-400 transition-colors">
                              <img src={img.url} alt="Imagen generada" loading="lazy" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                        {loadingMoreHistory && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
                          </div>
                        )}
                        {hasMoreHistory && !loadingMoreHistory && (
                          <div className="flex items-center justify-center py-3">
                            <button type="button" onClick={loadMoreHistoryImages} className="text-[13px] text-[#1644EB] hover:opacity-70 transition-opacity">Cargar más</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {pickerScreen === 'confirm' && pickerSelectedImage && (
                <div className="flex flex-col items-center gap-4">
                  <img src={pickerSelectedImage.url} alt="Imagen seleccionada" className="max-w-full max-h-[55vh] object-contain rounded-md" />
                  <div className="w-full space-y-2">
                    <button type="button" onClick={() => { if (pickerSelectedImage.isBase64) handleApplyImage(); else handleApplyImageFromUrl(pickerSelectedImage.url); }} className="w-full h-11 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">
                      Utilizar como {imagePickerTarget.frameType === 'start_frame' ? 'imagen inicial' : 'imagen final'}
                    </button>
                    {pickerSelectedImage.id && (
                      <button type="button" onClick={() => { setPickerScreen('edit'); setEditInstruction(''); }} className="w-full h-11 bg-white border border-neutral-200 text-brand-black/80 rounded-md text-[15px] font-medium shadow-sm hover:bg-neutral-50 transition-colors">
                        Editar antes de usar
                      </button>
                    )}
                  </div>
                </div>
              )}
              {pickerScreen === 'edit' && pickerSelectedImage && (
                <div className="flex flex-col gap-4">
                  <img src={pickerSelectedImage.url} alt="Imagen a editar" className="max-w-full max-h-[55vh] object-contain rounded-md mx-auto" />
                  <div className="space-y-3">
                    <p className="text-[13px] text-neutral-500">Describí el cambio que querés hacer a la imagen</p>
                    <div className={`flex items-end gap-2 bg-neutral-50 border border-neutral-200 rounded-md p-2 ${editSubmitting ? 'opacity-60' : ''}`}>
                      <textarea value={editInstruction} onChange={(e) => setEditInstruction(e.target.value)} placeholder="Ej: Que el fondo sea blanco..." disabled={editSubmitting} rows={3} className="flex-1 bg-transparent text-[15px] text-brand-black/80 resize-none focus:outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed" />
                      <button type="button" onClick={handleEditImage} disabled={editSubmitting || !editInstruction.trim()} className="shrink-0 w-9 h-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md flex items-center justify-center shadow-sm transition-colors">
                        {editSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
                      </button>
                    </div>
                    {editSubmitting && (<div className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin text-brand-primary" /><span className="text-[13px] text-neutral-500">Editando imagen...</span></div>)}
                  </div>
                </div>
              )}
              {pickerScreen === 'generate' && (
                <div className="flex flex-col gap-5">
                  <div className="space-y-3">
                    <p className="text-[13px] text-neutral-500">Describí la imagen que querés generar</p>
                    <div className="flex items-end gap-2 bg-neutral-50 border border-neutral-200 rounded-md p-2">
                      <textarea value={generateInstruction} onChange={(e) => setGenerateInstruction(e.target.value)} placeholder="Ej: Una persona caminando en la montaña al atardecer..." rows={3} className="flex-1 bg-transparent text-[15px] text-brand-black/80 resize-none focus:outline-none placeholder:text-neutral-400" />
                      <button type="button" disabled={!generateInstruction.trim()} className="shrink-0 w-9 h-9 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md flex items-center justify-center shadow-sm transition-colors opacity-50 cursor-not-allowed">
                        <Sparkles className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">Incluir en la generación</p>
                    <div className="space-y-1">
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-neutral-50 border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <ShoppingBag className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Producto</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-neutral-50 border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Modelo guardado</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-neutral-50 border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <Upload className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Subir imagen de referencia</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closePickerPanel} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
            </div>
          </div>

          {/* Mobile Slide — portalled, exact PruebaNuevaInterfaz pattern */}
          <div
            className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${pickerAnimated ? 'opacity-100' : 'opacity-0'}`}
            onClick={closePickerPanel}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
            style={{
              height: 'calc(100dvh - 1rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform: pickerDragY > 0
                ? `translateY(${pickerDragY}px)`
                : pickerAnimated ? 'translateY(0)' : 'translateY(100%)',
              transition: pickerDragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            <div
              className="py-2 cursor-grab active:cursor-grabbing"
              onTouchStart={handlePickerTouchStart}
              onTouchMove={handlePickerTouchMove}
              onTouchEnd={handlePickerTouchEnd}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            <div className="flex items-center justify-between px-4 pb-3">
              {pickerScreen !== 'gallery' ? (
                <button type="button" onClick={() => { if (pickerScreen === 'edit') { setPickerScreen('confirm'); setEditInstruction(''); } else if (pickerScreen === 'generate') { setPickerScreen('gallery'); setGenerateInstruction(''); } else { setPickerScreen('gallery'); setPickerSelectedImage(null); } }} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Atrás</button>
              ) : (
                <button type="button" onClick={closePickerPanel} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
              )}
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                {imagePickerTarget.frameType === 'start_frame' ? 'Imagen inicial' : 'Imagen final'}
              </h3>
              <div className="w-20" />
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4" onScroll={(e) => {
              if (!hasMoreHistory || loadingMoreHistory || pickerScreen !== 'gallery') return;
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 150) loadMoreHistoryImages();
            }}>
              {pickerScreen === 'gallery' && (
                <div className="space-y-5">
                  <p className="text-[13px] text-neutral-500">Elegí una imagen existente o generá una nueva para usar como {imagePickerTarget.frameType === 'start_frame' ? 'inicio' : 'final'} de tu video.</p>
                  <div className="space-y-1">
                    <button type="button" onClick={() => { setPickerScreen('generate'); setGenerateInstruction(''); }} className="w-full flex items-center gap-3 px-3 py-3 text-left transition-colors active:bg-white/60 rounded-md bg-white">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-brand-primary text-white">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <p className="text-[15px] font-medium text-neutral-900">Generar imagen</p>
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} className="w-full flex items-center gap-3 px-3 py-3 text-left transition-colors active:bg-white/60 rounded-md bg-white">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
                        <Upload className="w-4 h-4" />
                      </div>
                      <p className="text-[15px] font-medium text-neutral-900">{uploadingFile ? 'Subiendo...' : 'Subir mi propia imagen'}</p>
                    </button>
                  </div>
                  {usedInVideoImages.length > 0 && (
                    <div>
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Usadas en este video</p>
                      <div className="grid grid-cols-3 gap-2">
                        {usedInVideoImages.map((img, idx) => (
                          <button key={`used-${idx}`} type="button" onClick={() => { setPickerSelectedImage({ url: `data:image/png;base64,${img.b64}`, isBase64: true }); setPickerScreen('confirm'); }} className="relative aspect-square rounded-sm overflow-hidden border border-neutral-200 active:border-neutral-400 transition-colors">
                            <img src={`data:image/png;base64,${img.b64}`} alt={img.label} className="w-full h-full object-cover" />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent px-1.5 pb-1 pt-4">
                              <span className="text-[11px] font-medium text-white/90 line-clamp-1">{img.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Imágenes generadas</p>
                    {loadingHistory ? (
                      <div className="grid grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => (<div key={i} className="aspect-square bg-neutral-100 rounded-sm animate-pulse" />))}</div>
                    ) : historyImages.length === 0 ? (
                      <div className="text-center py-8"><ImageIcon className="w-10 h-10 text-neutral-300 mx-auto mb-2" /><p className="text-[13px] text-neutral-400">No hay imágenes generadas aún</p></div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          {historyImages.map((img) => (
                            <button key={img.id} type="button" onClick={() => { setPickerSelectedImage({ url: img.url, id: img.id }); setPickerScreen('confirm'); }} className="relative aspect-square rounded-sm overflow-hidden border border-neutral-200 active:border-neutral-400 transition-colors">
                              <img src={img.url} alt="Imagen generada" loading="lazy" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                        {loadingMoreHistory && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
                          </div>
                        )}
                        {hasMoreHistory && !loadingMoreHistory && (
                          <div className="flex items-center justify-center py-3">
                            <button type="button" onClick={loadMoreHistoryImages} className="text-[13px] text-[#1644EB] active:opacity-50 transition-opacity">Cargar más</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {pickerScreen === 'confirm' && pickerSelectedImage && (
                <div className="flex flex-col items-center gap-4">
                  <img src={pickerSelectedImage.url} alt="Imagen seleccionada" className="max-w-full max-h-[55vh] object-contain rounded-md" />
                  <div className="w-full space-y-2">
                    <button type="button" onClick={() => { if (pickerSelectedImage.isBase64) handleApplyImage(); else handleApplyImageFromUrl(pickerSelectedImage.url); }} className="w-full h-11 bg-brand-primary active:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">
                      Utilizar como {imagePickerTarget.frameType === 'start_frame' ? 'imagen inicial' : 'imagen final'}
                    </button>
                    {pickerSelectedImage.id && (
                      <button type="button" onClick={() => { setPickerScreen('edit'); setEditInstruction(''); }} className="w-full h-11 bg-white border border-neutral-200 text-brand-black/80 rounded-md text-[15px] font-medium shadow-sm active:bg-neutral-50 transition-colors">
                        Editar antes de usar
                      </button>
                    )}
                  </div>
                </div>
              )}
              {pickerScreen === 'edit' && pickerSelectedImage && (
                <div className="flex flex-col gap-4">
                  <img src={pickerSelectedImage.url} alt="Imagen a editar" className="max-w-full max-h-[55vh] object-contain rounded-md mx-auto" />
                  <div className="space-y-3">
                    <p className="text-[13px] text-neutral-500">Describí el cambio que querés hacer a la imagen</p>
                    <div className={`flex items-end gap-2 bg-white border border-neutral-200 rounded-md p-2 ${editSubmitting ? 'opacity-60' : ''}`}>
                      <textarea value={editInstruction} onChange={(e) => setEditInstruction(e.target.value)} placeholder="Ej: Que el fondo sea blanco..." disabled={editSubmitting} rows={3} className="flex-1 bg-transparent text-[15px] text-brand-black/80 resize-none focus:outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed" />
                      <button type="button" onClick={handleEditImage} disabled={editSubmitting || !editInstruction.trim()} className="shrink-0 w-9 h-9 bg-brand-primary active:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md flex items-center justify-center shadow-sm transition-colors">
                        {editSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
                      </button>
                    </div>
                    {editSubmitting && (<div className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin text-brand-primary" /><span className="text-[13px] text-neutral-500">Editando imagen...</span></div>)}
                  </div>
                </div>
              )}
              {pickerScreen === 'generate' && (
                <div className="flex flex-col gap-5">
                  <div className="space-y-3">
                    <p className="text-[13px] text-neutral-500">Describí la imagen que querés generar</p>
                    <div className="flex items-end gap-2 bg-white border border-neutral-200 rounded-md p-2">
                      <textarea value={generateInstruction} onChange={(e) => setGenerateInstruction(e.target.value)} placeholder="Ej: Una persona caminando en la montaña al atardecer..." rows={3} className="flex-1 bg-transparent text-[15px] text-brand-black/80 resize-none focus:outline-none placeholder:text-neutral-400" />
                      <button type="button" disabled={!generateInstruction.trim()} className="shrink-0 w-9 h-9 bg-brand-primary active:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md flex items-center justify-center shadow-sm transition-colors opacity-50 cursor-not-allowed">
                        <Sparkles className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">Incluir en la generación</p>
                    <div className="space-y-1">
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-white border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <ShoppingBag className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Producto</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-white border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Modelo guardado</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                      <button type="button" disabled className="w-full flex items-center gap-3 px-3 py-3 text-left rounded-md bg-white border border-neutral-200 opacity-50 cursor-not-allowed">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-400">
                          <Upload className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-[15px] font-medium text-neutral-400">Subir imagen de referencia</p>
                          <p className="text-[13px] text-neutral-400">Próximamente</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Loading Animation */}
      {generating && (
        <LoadingAnimation
          message="Generando video cinematográfico..."
          subMessage="Esto puede tardar unos minutos. No cierres esta ventana."
          durationMs={300000}
          completeNow={completeProgressNow}
          showCountdown
          onCompleted={() => {
            if (willShowResultsRef.current) {
              willShowResultsRef.current = false;
              setShowResults(true);
            }
            setGenerating(false);
            generatingLockRef.current = false;
          }}
        />
      )}

      {/* Error message */}
      {genError && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-red-50 border border-red-200 rounded-[13px] p-4 z-50">
          <p className="text-[15px] font-medium text-red-600">Error al generar el video</p>
          <p className="text-[13px] text-red-600 mt-1">{genError}</p>
        </div>
      )}
    </div>
  );
};

export default PlanificadorVideoCinematografico;
